import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';

const round2 = (x) => Math.round(x * 100) / 100;
const eur = (value) => `${value < 0 ? '-' : ''}€${Math.abs(value).toFixed(2)}`;
const shortDate = (day) => (day ? `${day.slice(8, 10)}/${day.slice(5, 7)}` : '');

// IVA, other taxes by name, then the client's canon; IVA and canon always have their entry (€0.00 when none applies).
const taxRows = (costs) => {
  const rows = costs.filter((cost) => cost.component !== 'base');
  for (const component of ['iva', 'canon']) {
    if (!rows.some((cost) => cost.component === component)) rows.push({ component, tag: null, amount: 0 });
  }
  const rank = (cost) => ({ iva: 0, canon: 2 }[cost.component] ?? 1);
  return rows.sort((a, b) => rank(a) - rank(b) || a.component.localeCompare(b.component) || (a.tag || '').localeCompare(b.tag || ''));
};

/**
 * Body of the drawer's margin card: today's gross profit per unit at the lane's planogram price, how
 * the price splits into each tax and fee (short name; the product tag on hover) and the supplier cost.
 * The last sale's price is noted when it differs.
 */
const SlotUnitEconomics = ({ data }) => {
  const { t } = useTranslation();

  const label = (component) => t(`costComponent_${component}`, { defaultValue: component });
  const taxes = taxRows(data.costs || []);
  const netRevenue = round2(data.price - taxes.reduce((sum, cost) => sum + cost.amount, 0));
  const profit = data.baseCost !== null ? round2(netRevenue - data.baseCost) : null;
  const marginPct = profit !== null && netRevenue > 0 ? (profit / netRevenue) * 100 : null;
  const saleDiffers = data.priceSource === 'planogram' && data.lastSale && round2(data.lastSale.price) !== round2(data.price);

  const parts = [
    { key: 'base', short: label('base'), full: label('base'), amount: data.baseCost },
    ...taxes.map((cost) => ({
      key: `${cost.component}|${cost.tag || ''}`,
      // "IBEE 1: 330 cc - Sucre >8 g/100 ml" reads as "IBEE 1"; a tag without a level reads as its tax
      short: cost.tag?.includes(':') ? cost.tag.split(':')[0] : label(cost.component),
      full: cost.tag || label(cost.component),
      amount: cost.amount,
    })),
  ];

  return (
    <>
      <Tooltip title={t('machines_unitEconomicsTitle')} placement="top-start">
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography sx={{
            fontSize: '18px', fontWeight: 600,
            color: profit === null ? colors.text.primary : profit >= 0 ? colors.text.primary : colors.status.error,
          }}>
            {profit !== null ? `${eur(profit)}/u` : '–'}
          </Typography>
          {marginPct !== null && (
            <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>{marginPct.toFixed(0)} %</Typography>
          )}
        </Box>
      </Tooltip>

      <Typography component="div" sx={{ fontSize: '11px', color: colors.text.secondary, lineHeight: 1.5, mt: 0.25 }}>
        <span style={{ whiteSpace: 'nowrap' }}>{t('machines_unitPrice')} {eur(data.price)}</span>
        {parts.map((part) => (
          <Tooltip key={part.key} title={part.full}>
            <span>{' − '}<span style={{ whiteSpace: 'nowrap' }}>{part.short} {part.amount !== null ? eur(part.amount) : '–'}</span></span>
          </Tooltip>
        ))}
      </Typography>

      {(saleDiffers || data.priceSource === 'lastSale') && (
        <Typography sx={{ fontSize: '11px', color: colors.status.warning, mt: 0.25 }}>
          {data.priceSource === 'lastSale'
            ? t('machines_priceFromLastSale', { date: shortDate(data.priceDate) })
            : t('machines_lastSalePrice', { price: eur(data.lastSale.price), date: shortDate(data.lastSale.date) })}
        </Typography>
      )}
    </>
  );
};

export default SlotUnitEconomics;
